import { Link } from 'react-router-dom';

export function Home() {
    return (
        <>
            <Hero />
            <Capabilities />
            <Materials />
            <Process />
            <FinalCta />
        </>
    );
}

function Hero() {
    return (
        <section className="relative overflow-hidden bg-ptm-ink text-white">
            <div
                className="absolute inset-0 opacity-[0.08]"
                style={{
                    backgroundImage:
                        'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
                    backgroundSize: '48px 48px'
                }}
                aria-hidden="true"
            />
            <div
                className="absolute -top-40 -right-40 h-[32rem] w-[32rem] rounded-full bg-ptm-accent/20 blur-3xl"
                aria-hidden="true"
            />

            <div className="relative mx-auto max-w-6xl px-6 py-28 md:py-40">
                <div className="max-w-3xl">
                    <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-white/80 backdrop-blur">
                        <span className="h-1.5 w-1.5 rounded-full bg-ptm-accent" />
                        Melbourne-based precision machining
                    </div>
                    <h1 className="text-5xl font-bold leading-[1.05] tracking-tight md:text-7xl">
                        Parts made right.<br />
                        <span className="bg-gradient-to-r from-ptm-accent to-orange-400 bg-clip-text text-transparent">
                            Built to spec.
                        </span>
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg text-white/70 md:text-xl">
                        CNC milling and turning for prototypes and production runs. Upload your
                        drawings or CAD models and we'll send back an itemised quote.
                    </p>
                    <div className="mt-12 flex flex-wrap gap-4">
                        <Link
                            to="/quote"
                            className="group inline-flex items-center gap-2 rounded-md bg-ptm-accent px-7 py-4 font-semibold text-white no-underline shadow-lg shadow-ptm-accent/20 transition hover:bg-orange-500 hover:shadow-ptm-accent/40"
                        >
                            Request a Quote
                            <span className="transition group-hover:translate-x-0.5">&rarr;</span>
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Capabilities() {
    return (
        <section className="bg-slate-50">
            <div className="mx-auto max-w-6xl px-6 py-24">
                <SectionHeader
                    eyebrow="Capabilities"
                    title="Milling and turning"
                    description="Whatever combination your part needs, you get a single quote, a single point of contact, and a single shipment."
                />

                <div className="mt-16 grid gap-8 md:grid-cols-2">
                    <CapabilityCard
                        tag="Milling"
                        title="CNC Milling"
                        specs={[
                            { label: 'Axes', value: '3-axis' },
                            { label: 'Work envelope', value: '500 × 400 mm' },
                            { label: 'Tolerance', value: '±0.05 mm' }
                        ]}
                        body="Pockets, slots, bores, profiled surfaces, and complex geometries. Aluminium, steel, stainless, brass, and engineering plastics."
                    />
                    <CapabilityCard
                        tag="Turning"
                        title="CNC Turning"
                        specs={[
                            { label: 'Max diameter', value: 'Ø250 mm' },
                            { label: 'Max length', value: '600 mm' },
                            { label: 'Tolerance', value: '±0.025 mm' }
                        ]}
                        body="Shafts, bushings, adapters, fasteners, and rotationally symmetric parts. Single-op or multi-setup with optional live-tooling."
                    />
                </div>
            </div>
        </section>
    );
}

type CapabilityCardProps = {
    tag: string;
    title: string;
    specs: Array<{ label: string; value: string }>;
    body: string;
};

function CapabilityCard({ tag, title, specs, body }: CapabilityCardProps) {
    return (
        <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
            <div className="absolute right-0 top-0 h-24 w-24 -translate-y-8 translate-x-8 rounded-full bg-ptm-accent/5 transition group-hover:bg-ptm-accent/10" />
            <div className="relative">
                <div className="inline-block rounded-full bg-ptm-accent/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-ptm-accent">
                    {tag}
                </div>
                <h3 className="mt-4 text-2xl font-bold tracking-tight">{title}</h3>
                <p className="mt-3 text-slate-600">{body}</p>

                <dl className="mt-6 grid grid-cols-2 gap-4 border-t border-slate-100 pt-6">
                    {specs.map((s) => (
                        <div key={s.label}>
                            <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">
                                {s.label}
                            </dt>
                            <dd className="mt-1 font-semibold text-ptm-ink">{s.value}</dd>
                        </div>
                    ))}
                </dl>
            </div>
        </div>
    );
}

function Materials() {
    const summary: Array<{ heading: string; detail: string }> = [
        { heading: 'Aluminium', detail: '6061, 7075 and other common grades' },
        { heading: 'Steel', detail: '1018, 1045 mild and 4140 pre-hard' },
        { heading: 'Non-ferrous', detail: 'Brass, copper, bronze, titanium' },
        { heading: 'Plastics', detail: 'Acetal, nylon, PEEK, polycarbonate' }
    ];
    return (
        <section className="bg-white">
            <div className="mx-auto max-w-6xl px-6 py-24">
                <SectionHeader
                    eyebrow="Materials"
                    title="Metals and plastics"
                    description="Everyday engineering materials kept on the shelf. Exotic or bulk orders sourced to spec."
                />
                <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                    {summary.map((s) => (
                        <div
                            key={s.heading}
                            className="rounded-xl border border-slate-200 bg-slate-50 p-6"
                        >
                            <h3 className="text-sm font-semibold uppercase tracking-wider text-ptm-accent">
                                {s.heading}
                            </h3>
                            <p className="mt-3 text-slate-700">{s.detail}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Process() {
    const steps: Array<{ n: string; title: string; body: string }> = [
        {
            n: '01',
            title: 'Upload',
            body: 'Drop in STEP, SolidWorks, STL, DWG, or a PDF drawing. Add quantity, material, finish, and any notes.'
        },
        {
            n: '02',
            title: 'Quote',
            body: 'Our team reviews your files, runs DFM checks, and sends back an itemised quote.'
        },
        {
            n: '03',
            title: 'Approve & pay',
            body: 'Approve the quote and pay securely online.'
        },
        {
            n: '04',
            title: 'Manufacture & ship',
            body: 'Track your order through production, QA, and shipping from your portal.'
        }
    ];
    return (
        <section className="bg-ptm-ink text-white">
            <div className="mx-auto max-w-6xl px-6 py-24">
                <SectionHeader
                    eyebrow="How it works"
                    title="From drawing to doorstep"
                    description="A transparent, four-step workflow with you in the loop at every stage."
                    dark
                />
                <ol className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
                    {steps.map((s) => (
                        <li key={s.n} className="relative">
                            <div className="text-sm font-semibold tracking-wider text-ptm-accent">
                                {s.n}
                            </div>
                            <div className="mt-2 h-px w-full bg-white/10" />
                            <h3 className="mt-4 text-lg font-bold">{s.title}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-white/70">{s.body}</p>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    );
}

function FinalCta() {
    return (
        <section className="bg-white">
            <div className="mx-auto max-w-6xl px-6 py-24">
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-ptm-ink via-ptm-steel to-ptm-ink p-12 shadow-xl md:p-16">
                    <div
                        className="absolute inset-0 opacity-10"
                        style={{
                            backgroundImage:
                                'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)',
                            backgroundSize: '24px 24px'
                        }}
                        aria-hidden="true"
                    />
                    <div className="relative flex flex-col items-start gap-8 md:flex-row md:items-center md:justify-between">
                        <div className="max-w-2xl">
                            <h2 className="text-3xl font-bold tracking-tight text-white md:text-4xl">
                                Ready to make parts?
                            </h2>
                            <p className="mt-3 text-lg text-white/70">
                                Upload your drawings or CAD models and get an itemised quote back.
                            </p>
                        </div>
                        <Link
                            to="/quote"
                            className="group inline-flex shrink-0 items-center gap-2 rounded-md bg-ptm-accent px-7 py-4 font-semibold text-white no-underline shadow-lg shadow-ptm-accent/30 transition hover:bg-orange-500"
                        >
                            Request a Quote
                            <span className="transition group-hover:translate-x-0.5">&rarr;</span>
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

function SectionHeader({
    eyebrow,
    title,
    description,
    dark = false
}: {
    eyebrow: string;
    title: string;
    description: string;
    dark?: boolean;
}) {
    return (
        <div className="max-w-3xl">
            <div
                className={`text-sm font-semibold uppercase tracking-wider ${
                    dark ? 'text-ptm-accent' : 'text-ptm-accent'
                }`}
            >
                {eyebrow}
            </div>
            <h2
                className={`mt-3 text-4xl font-bold tracking-tight md:text-5xl ${
                    dark ? 'text-white' : 'text-ptm-ink'
                }`}
            >
                {title}
            </h2>
            <p className={`mt-4 text-lg ${dark ? 'text-white/70' : 'text-slate-600'}`}>
                {description}
            </p>
        </div>
    );
}
